// PvDakTile — PV van één dak (remote_pv-bron) als KPI-tegel in de KpiColumn.
//
// Alleen op een site met remote_pv (Erdotex): daar hangt de PV achter aparte
// omvormersets die door meet-Pi's (rol pv_monitor) worden gelezen; de accu-Pi
// haalt hun snapshot op (remote_pv.py). VJ/NS/HO dragen de sleutel niet en
// renderen deze tegel nooit.
//
// Opbouw volgt de bestaande tegels: kpi-tile met amber linkerrand (zelfde
// accent als Eigenverbruik PV), kpi-label/kpi-value/kpi-sub, en de
// per-omvormerregels in de breakdown-row-stijl van CombinedSavingsTile,
// uitklapbaar zodat de kolom op desktop niet langer wordt dan nodig.

import type { CSSProperties } from 'react';
import { fmtKw, fmtKwh } from '../../lib/format';
import type { RemotePvInverter, RemotePvSource } from '../../api/types';

const PV_ACCENT = '#f59e0b';

function leeftijd(age: number | null | undefined): string {
  if (age == null) return 'nog geen meting';
  if (age < 90) return `${Math.round(age)} s geleden`;
  if (age < 3600) return `${Math.round(age / 60)} min geleden`;
  return `${Math.round(age / 3600)} uur geleden`;
}

function kwParts(w: number | null | undefined): [string, string] {
  if (w == null) return ['—', ''];
  const s = fmtKw(w);
  const i = s.lastIndexOf(' ');
  return i > 0 ? [s.slice(0, i), s.slice(i + 1)] : [s, ''];
}

function InverterRow({ inv, bronVers }: { inv: RemotePvInverter; bronVers: boolean }) {
  const stale = !bronVers || inv.stale || !inv.ok;
  const vandaag = inv.today_kwh == null ? '—' : fmtKwh(inv.today_kwh);
  return (
    <div className="breakdown-row">
      <span className="breakdown-label">{inv.name ?? `Omvormer ${inv.id ?? '?'}`}</span>
      <span className="breakdown-value">
        {stale
          ? <span className="pv-dak-stale">geen actuele data</span>
          : (inv.power_w == null ? '—' : fmtKw(inv.power_w))}
        {' · '}{vandaag}
      </span>
    </div>
  );
}

export function PvDakTile({ src }: { src: RemotePvSource }) {
  const invs = src.inverters ?? [];
  const nTotal = src.n_total ?? invs.length;
  const nOk = src.ok
    ? (src.n_ok ?? invs.filter((i) => i.ok && !i.stale).length)
    : 0;
  const [num, unit] = kwParts(src.ok ? src.power_w : null);
  const vandaag = src.today_kwh == null ? '—' : fmtKwh(src.today_kwh);
  return (
    <div
      className="kpi-tile pv-dak-tile"
      style={{ ['--accent' as string]: src.ok ? PV_ACCENT : 'var(--text-tertiary)' } as CSSProperties}
    >
      <div className="kpi-label">PV {src.label ?? src.name}</div>
      <div className="kpi-value">
        {num}{unit && <span className="unit"> {unit}</span>}
      </div>
      <div className="kpi-sub">vandaag {vandaag}</div>
      {src.ok
        ? <div className="kpi-sub">{nOk}/{nTotal} omvormers actueel</div>
        : <div className="kpi-sub down">verouderd · {leeftijd(src.age_s)}</div>}
      {invs.length > 0 && (
        <details className="pv-dak-details">
          <summary className="kpi-sub">per omvormer</summary>
          <div className="pv-dak-rows">
            {invs.map((inv, i) => (
              <InverterRow key={`${inv.id ?? i}`} inv={inv} bronVers={src.ok} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
